import { useEffect } from "react";

import { FormProvider, useFormContext } from "react-hook-form";

import Box from "@/components/Box";
import CustomerPage from "@/components/CustomerPage";
import Divider from "@/components/Divider";
import { ExternalTextLink, InternalTextLink } from "@/components/Links";
import { theme } from "@/core/theme";
import { CMP_SIGNIN_URL } from "@/core/urls";
import { REGENERUS_PRIVACY_URL, REGENERUS_TERMS_OF_USE_URL } from "@/core/urls";
import { SPECIALISMS_AND_PRACTITIONER_TYPES_QUERY } from "@/graphql/accounts";
import useScrollToError from "@/hooks/form/useScrollToError";
import Asterisk from "@/tpo/Asterisk";
import Checkbox from "@/tpo/Checkbox";
import ChevronComponent from "@/tpo/Chevron";
import ControlledFormField from "@/tpo/ControlledFormField";
import ControlledRadioGroup from "@/tpo/ControlledRadioGroup";
import DateInput from "@/tpo/DateInput";
import FloatingLabelInput from "@/tpo/FloatingLabelInput";
import FormControl from "@/tpo/FormControl";
import { Error } from "@/tpo/InputWrapper";
import FloatingLabelPasswordInput from "@/tpo/PasswordInput";
import ShippingCountrySelect from "@/tpo/ShippingCountrySelect";
import Spacer from "@/tpo/Spacer";
import Stack from "@/tpo/Stack";
import BasePractitionerTypeField from "@/tpo/partnerDashboard/practitioner/Application/ui/PractitionerTypeField";
import ButtonV2 from "@/v2/Buttons";
import { useQuery } from "@apollo/client";
import { createGlobalStyle } from "styled-components";

import { buildPartialDateValue } from "../buildPractitionerApplicationMutationInput";
import ProfessionalBodyField from "./ProfessionalBodyField";
import ProfessionalVerification from "./ProfessionalVerification";
import QualificationProof from "./QualificationProof";
import RequiredFieldsNotice from "./RequiredFieldsNotice";
import SpecialismsField from "./SpecialismsField";
import { PRACTITIONER_APPLICATION_DROPDOWN_MAX_WIDTH } from "./practitionerApplicationDropdownLayout";

const GlobalStyle = createGlobalStyle`
  .PoweredByRegenerusBanner svg {
    fill: black;
  }
`;

const PRACTITIONER_APPLICATION_RADIO_SIZE = 28;

function RegisterDocumentFormFields() {
  const { register } = useFormContext();

  useEffect(() => {
    register("documents");
    register("insuranceEvidence");
    register("professionalBodies");
  }, [register]);

  return null;
}

function PractitionerTypeField({ practitionerTypes }) {
  const { setValue, clearErrors } = useFormContext();

  return (
    <BasePractitionerTypeField
      practitionerTypes={practitionerTypes}
      onChange={val => {
        setValue("practitionerType", val);
        setValue("documents", []);
        clearErrors(["practitionerType", "documents"]);
      }}
    />
  );
}

function ProfessionalBodySelectField({ professionalBodies }) {
  const { clearErrors, getValues, setValue } = useFormContext();

  return (
    <ProfessionalBodyField
      professionalBodies={professionalBodies}
      onSelect={bodyId => {
        const current = getValues("professionalBodies") || [];
        if (current.some(membership => String(membership.id) === String(bodyId))) {
          return;
        }
        setValue(
          "professionalBodies",
          [...current, { id: bodyId == null ? bodyId : String(bodyId), registrationNumber: "" }],
          {
            shouldDirty: true,
            shouldValidate: true
          }
        );
        clearErrors(["professionalBodies", "professionalBodyMemberships"]);
      }}
      onRemove={bodyId => {
        const current = getValues("professionalBodies") || [];
        setValue(
          "professionalBodies",
          current.filter(membership => String(membership.id) !== String(bodyId)),
          { shouldDirty: true, shouldValidate: true }
        );
      }}
      onRegistrationNumberChange={(bodyId, registrationNumber) => {
        const current = getValues("professionalBodies") || [];
        setValue(
          "professionalBodies",
          current.map(membership =>
            String(membership.id) === String(bodyId)
              ? { ...membership, registrationNumber }
              : membership
          ),
          { shouldDirty: true, shouldValidate: true }
        );
      }}
    />
  );
}

export default function Common({
  api,
  showLoginLink = false,
  showPasswordFields = false,
  readOnlyFields = []
}) {
  const { data: { practitionerTypes = [], professionalBodyOptions = [], specialisms = [] } = {} } =
    useQuery(SPECIALISMS_AND_PRACTITIONER_TYPES_QUERY);

  useScrollToError(api);

  return (
    <FormProvider {...api.methods}>
      <RegisterDocumentFormFields />
      <CustomerPage
        bg="haze"
        jumbotronProps={{
          bg: "partners",
          title: "Practitioner application",
          color: "dark"
        }}
      >
        <GlobalStyle />
        <Box pt={theme.spacing.section.pt} pb={theme.spacing.section.pb} px={[20, 20, "5.5vw"]}>
          <Box maxWidth={760} mx="auto">
            <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
              Register for a Practitioner Account
            </Box>
            <Spacer py={2} />
            <Box
              as="ul"
              fontFamily="gilroyMedium"
              fontSize={16}
              listStyle="disc"
              style={{
                listStylePosition: "inside"
              }}
            >
              The Practitioner level of access is for qualified health professionals.
              <Spacer py={2} />
              <Box as="li" fontFamily="inherit">
                Full access to our exclusive suite of 200+ tests at trade prices.
              </Box>
              <Box as="li" fontFamily="inherit">
                Order & manage tests on behalf of your patients.
              </Box>
              <Box as="li" fontFamily="inherit">
                Review and release test results via the patient platform
              </Box>
            </Box>
            <Spacer py={20} />
            <Stack
              as="form"
              gap={[20, 20, 40]}
              onSubmit={e => {
                api.methods.clearErrors();
                api.methods.handleSubmit(api.onSubmit, api.onInvalid)(e);
              }}
            >
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <RequiredFieldsNotice />
                <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                  Your details
                  <Asterisk />
                </Box>
                <FormControl
                  as="fieldset"
                  labelProps={{
                    fontFamily: "gilroyBold",
                    fontSize: [28],
                    lineHeight: "130%"
                  }}
                  fields={
                    <>
                      <ControlledFormField
                        name="firstName"
                        label="First name"
                        Component={FloatingLabelInput}
                        editable={!readOnlyFields.includes("firstName")}
                      />
                      <ControlledFormField
                        name="lastName"
                        label="Last name"
                        Component={FloatingLabelInput}
                        editable={!readOnlyFields.includes("lastName")}
                      />
                      <ControlledFormField
                        name="email"
                        label="Email address"
                        Component={FloatingLabelInput}
                        editable={!readOnlyFields.includes("email")}
                      />
                      <ControlledFormField
                        name="phoneNumber"
                        label="Contact number"
                        Component={FloatingLabelInput}
                      />
                      <ControlledFormField
                        name="jobRole"
                        label="Job title / Role"
                        Component={FloatingLabelInput}
                      />
                      <ControlledFormField
                        name="businessName"
                        label="Organisation / clinic name"
                        Component={FloatingLabelInput}
                      />
                      <ControlledFormField
                        name="businessLocationCity"
                        label="Location City"
                        Component={FloatingLabelInput}
                      />
                      <ControlledFormField
                        name="businessCountry"
                        placeholder="Select option"
                        Component={ShippingCountrySelect}
                        valueField="id"
                        fullWidth
                        width="100%"
                        maxWidth={PRACTITIONER_APPLICATION_DROPDOWN_MAX_WIDTH}
                      />
                    </>
                  }
                />
                {showPasswordFields && (
                  <FormControl
                    as="fieldset"
                    label={
                      <Box fontFamily="gilroyBold" fontSize={[18]}>
                        Password
                        <Asterisk />
                      </Box>
                    }
                    fields={
                      <>
                        <ControlledFormField
                          name="password"
                          label="Password"
                          Component={FloatingLabelPasswordInput}
                        />
                        <ControlledFormField
                          name="confirmPassword"
                          label="Confirm Password"
                          Component={FloatingLabelPasswordInput}
                        />
                      </>
                    }
                  />
                )}
                <ControlledFormField
                  name="dateOfBirth"
                  Component={DateInput}
                  getDay={dateStr => (dateStr ? dateStr.split("-")[2] : "")}
                  getMonth={dateStr => (dateStr ? dateStr.split("-")[1] : "")}
                  getYear={dateStr => (dateStr ? dateStr.split("-")[0] : "")}
                  constructDate={buildPartialDateValue}
                  label="Date of birth"
                  labelProps={{
                    fontFamily: "gilroyBold",
                    fontSize: 18,
                    mb: 20
                  }}
                  disabled={readOnlyFields.includes("dateOfBirth")}
                />
                <ControlledRadioGroup
                  radioSize={PRACTITIONER_APPLICATION_RADIO_SIZE}
                  Container={Stack}
                  containerProps={{
                    gap: 20
                  }}
                  name="gender"
                  label="Gender"
                  labelProps={{
                    fontFamily: "gilroyBold",
                    fontSize: 18,
                    mb: 20
                  }}
                  values={[
                    {
                      label: "Male",
                      value: "Male"
                    },
                    {
                      label: "Female",
                      value: "Female"
                    }
                  ]}
                  disabled={readOnlyFields.includes("gender")}
                />
                {showLoginLink && (
                  <Box fontFamily="gilroyMedium" fontSize={16}>
                    If you already have an account{" "}
                    <InternalTextLink href={CMP_SIGNIN_URL}>sign in</InternalTextLink>.
                  </Box>
                )}
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <Box fontFamily="gilroyBold" fontSize={[28, 28, 36]}>
                  Your Professional Practice
                </Box>
                <FormControl
                  label={
                    <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="26px">
                      What is your profession?
                    </Box>
                  }
                  spaceAboveHelpText={2}
                  helpText={
                    <Box fontFamily="gilroyMedium" fontSize={16}>
                      Select the option that best describes your primary professional role.
                    </Box>
                  }
                  spaceAboveChildren={[20, 20, 40]}
                >
                  <Stack alignItems="flex-start" gap={[20, 20, 40]}>
                    <PractitionerTypeField practitionerTypes={practitionerTypes} />
                    {!!professionalBodyOptions.length && (
                      <FormControl
                        label={
                          <Box fontFamily="gilroyBold" fontSize={18} lineHeight="26px">
                            Professional registration / membership number
                          </Box>
                        }
                        spaceAboveHelpText={2}
                        helpText={
                          <Box fontFamily="gilroyMedium" fontSize={16}>
                            Select all bodies you belong to and provide a registration or membership
                            number where required.
                          </Box>
                        }
                        spaceAboveChildren={[20, 20, 40]}
                      >
                        <ProfessionalBodySelectField professionalBodies={professionalBodyOptions} />
                      </FormControl>
                    )}
                  </Stack>
                </FormControl>
                <QualificationProof practitionerTypes={practitionerTypes} />
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <FormControl
                  label={
                    <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="26px">
                      Your Practice
                    </Box>
                  }
                  spaceAboveHelpText={2}
                  helpText={
                    <Box fontFamily="gilroyMedium" fontSize={16}>
                      This information helps us tailor your account and practitioner support.
                    </Box>
                  }
                  spaceAboveChildren={[20, 20, 40]}
                >
                  <Stack gap={40}>
                    <ControlledRadioGroup
                      radioSize={PRACTITIONER_APPLICATION_RADIO_SIZE}
                      Container={Stack}
                      containerProps={{
                        gap: 20
                      }}
                      name="clientsPerMonth"
                      label="How many clients are you (or the clinic you work for) seeing currently per month?"
                      labelProps={{
                        fontFamily: "gilroyBold",
                        fontSize: 18,
                        mb: 20
                      }}
                      values={[
                        {
                          label: "1-20",
                          value: "1-20"
                        },
                        {
                          label: "31-40",
                          value: "31-40"
                        },
                        {
                          label: "40+",
                          value: "40+"
                        }
                      ]}
                    />
                    <Divider color="midGrey" />
                    <ControlledRadioGroup
                      radioSize={PRACTITIONER_APPLICATION_RADIO_SIZE}
                      Container={Stack}
                      containerProps={{
                        gap: 20
                      }}
                      name="orderTestingTimeline"
                      label="How soon do you wish to order testing?"
                      labelProps={{
                        fontFamily: "gilroyBold",
                        fontSize: 18,
                        mb: 20
                      }}
                      values={[
                        {
                          label: "Immediately",
                          value: "Immediately"
                        },
                        {
                          label: "Within the next 30 days",
                          value: "Within the next 30 days"
                        },
                        {
                          label: "Within 1–3 months",
                          value: "Within 1–3 months"
                        },
                        {
                          label: "Within 3–6 months",
                          value: "Within 3–6 months"
                        },
                        {
                          label: "I'm currently exploring options",
                          value: "I'm currently exploring options"
                        }
                      ]}
                    />
                    <Divider color="midGrey" />
                    <ControlledRadioGroup
                      radioSize={PRACTITIONER_APPLICATION_RADIO_SIZE}
                      Container={Stack}
                      containerProps={{
                        gap: 20
                      }}
                      name="expectedTestsPerMonth"
                      label="Approximately how many tests do you expect to order through Regenerus each month?"
                      labelProps={{
                        fontFamily: "gilroyBold",
                        fontSize: 18,
                        mb: 20
                      }}
                      values={[
                        {
                          label: "1-2",
                          value: "1-2"
                        },
                        {
                          label: "3-10",
                          value: "3-10"
                        },
                        {
                          label: "11-20",
                          value: "11-20"
                        },
                        {
                          label: "20+",
                          value: "20+"
                        },
                        {
                          label: "Not sure yet",
                          value: "Not sure yet"
                        }
                      ]}
                    />
                    <Divider color="midGrey" />
                    <Stack gap={20}>
                      <ControlledRadioGroup
                        radioSize={PRACTITIONER_APPLICATION_RADIO_SIZE}
                        Container={Stack}
                        containerProps={{
                          gap: 20
                        }}
                        name="supplementRecommendationMethod"
                        label="How do you currently recommend or supply supplements to patients?"
                        labelProps={{
                          fontFamily: "gilroyBold",
                          fontSize: 18,
                          mb: 20
                        }}
                        values={[
                          {
                            label: "I don't recommend supplements",
                            value: "I don't recommend supplements"
                          },
                          {
                            label: "I recommend products for patients to purchase themselves",
                            value: "I recommend products for patients to purchase themselves"
                          },
                          {
                            label: "I use an online dispensary",
                            value: "I use an online dispensary"
                          },
                          {
                            label: "Other",
                            value: "Other"
                          }
                        ]}
                      />
                      <ControlledFormField
                        name="supplementProvider"
                        label="Which dispensary or supplement provider do you currently use?"
                        Component={FloatingLabelInput}
                        labelTop={["-8px", "13px", "13px"]}
                      />
                    </Stack>
                  </Stack>
                </FormControl>
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <FormControl
                  label={
                    <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="26px">
                      Your Areas of Interest
                    </Box>
                  }
                  spaceAboveHelpText={2}
                  helpText={
                    <Box fontFamily="gilroyMedium" fontSize={16}>
                      Which areas of testing are you particularly interested in?
                    </Box>
                  }
                  spaceAboveChildren={[20, 20, 40]}
                >
                  <Stack alignItems="flex-start" gap={[20, 20, 40]}>
                    <SpecialismsField specialisms={specialisms} />
                  </Stack>
                </FormControl>
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <FormControl
                  label={
                    <Box fontFamily="gilroyBold" fontSize={[18, 18, 28]} lineHeight="26px">
                      Professional Verification
                    </Box>
                  }
                  spaceAboveHelpText={2}
                  helpText={
                    <Box fontFamily="gilroyMedium" fontSize={16}>
                      To protect patients and maintain access to practitioner-only testing, we
                      verify the professional status of practitioners registering with Regenerus.
                    </Box>
                  }
                  spaceAboveChildren={[20, 20, 40]}
                >
                  <ProfessionalVerification />
                </FormControl>
              </Stack>
              <Stack bg="white" pt={40} pb={80} px={[20, 20, 80]} gap={40} borderRadius={5}>
                <FormControl
                  fields={
                    <>
                      <ControlledFormField
                        label={
                          <>
                            By submitting this form, I confirm that I have read and agree to the{" "}
                            <InternalTextLink href={REGENERUS_TERMS_OF_USE_URL}>
                              Terms of Service
                            </InternalTextLink>{" "}
                            and{" "}
                            <InternalTextLink href={REGENERUS_PRIVACY_URL}>
                              Privacy Policy
                            </InternalTextLink>{" "}
                            , and I consent to receive health-related news, exclusive promotions,
                            and product updates from Regenerus Lab by email.
                          </>
                        }
                        name="acceptTerms"
                        Component={Checkbox}
                        styles={{
                          labelProps: {
                            fontSize: 14,
                            lineHeight: "24px"
                          },
                          wrapperProps: {
                            alignItems: "flex-start"
                          },
                          checkbox: {
                            size: "lg"
                          }
                        }}
                      />
                    </>
                  }
                />
                <ButtonV2
                  color="dark"
                  rightIcon={<ChevronComponent />}
                  size={["sm", "sm", "md"]}
                  mx="auto"
                >
                  Submit
                </ButtonV2>
                {!!api.nonFieldError && <Error error={api.nonFieldError} />}
              </Stack>
            </Stack>
          </Box>
        </Box>
      </CustomerPage>
    </FormProvider>
  );
}
